import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import PageMeta from '../../components/PageMeta';
import { toast } from 'sonner';

const STORAGE_KEY = 'ali_nutrition_application_draft';
const TOTAL_STEPS = 11;

const stepLabels = [
  'General Info', 'Health Goals', 'Medication', 'Medical History',
  'Family History', 'Past Lifestyle', 'Digestive', 'Liver & Hormonal',
  'Immune & Exercise', 'Food Diary', 'Review'
];

const initialForm = () => ({
  // Step 1
  name: '', address: '', mobile: '', email: '', date_of_birth: '', last_blood_test: '',
  occupation: '', smoker: '', height: '', weight: '',
  // Step 2
  stressful_events: '', colonoscopy_results: '', seen_nutritionist: '', health_concerns: '',
  // Step 3
  current_medications: '', past_medications: '', supplements: '',
  // Step 4
  medical_history: '',
  // Step 5
  family_history: {},
  // Step 6
  breastfed: '', childhood_vaccinations: '', happy_active_child: '', childhood_trauma: '',
  // Step 7
  digestive_profile: {},
  // Step 8
  liver_profile: {}, endocrine_profile: {},
  // Step 9
  immune_profile: {}, exercise_frequency: '', exercise_type: '', exercise_reason: '',
  // Step 10
  breakfast: '', breakfast_time: '', lunch: '', dinner: '', dinner_time: '',
  snacks: '', snack_times: '', other_foods: '', additional_info: '',
});

const NutritionApplication = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check for saved draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.name) {
          setShowRestore(true);
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Auto-save silently
  useEffect(() => {
    if (!submitted) {
      const timeout = setTimeout(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch (e) { /* ignore */ }
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [form, submitted]);

  const restoreDraft = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved) setForm({ ...initialForm(), ...saved });
    } catch (e) { /* ignore */ }
    setShowRestore(false);
  };

  const startFresh = () => {
    localStorage.removeItem(STORAGE_KEY);
    setForm(initialForm());
    setShowRestore(false);
  };

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const updateNested = (section, key, value) => setForm(prev => ({
    ...prev, [section]: { ...prev[section], [key]: value }
  }));

  const next = () => { if (step < TOTAL_STEPS) setStep(step + 1); window.scrollTo({ top: 0, behavior: 'instant' }); };
  const prev = () => { if (step > 1) setStep(step - 1); window.scrollTo({ top: 0, behavior: 'instant' }); };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please provide your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/nutrition-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (response.ok) {
        setSubmitted(true);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="nutrition-app-page">
        <PageMeta title="Application Submitted | Ali Dorgan" description="Thank you for submitting your nutritional therapy application." />
        <section className="py-20 md:py-28 bg-cream-100 min-h-[70vh] flex items-center">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-serif text-4xl font-light text-charcoal mb-6">Application Received</h1>
              <p className="text-charcoal/70 text-lg leading-relaxed mb-4">
                Thank you for completing the Nutritional Therapy questionnaire.
              </p>
              <p className="text-charcoal/60 text-base leading-relaxed mb-10">
                Ali will review your application and follow up with you directly to discuss suitability and next steps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-8 gap-2" data-testid="post-submit-whatsapp">
                  <a href="https://wa.me/353877727580?text=Hi%20Ali%2C%20I%27m%20interested%20in%20your%20Nutritional%20Therapy%20service." target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5" /> Message Ali
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full px-8 border-charcoal text-charcoal hover:bg-charcoal hover:text-white" data-testid="post-submit-home">
                  <a href="/">Back to Home</a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="nutrition-app-page">
      <PageMeta title="Apply for Nutritional Therapy | Ali Dorgan" description="Complete Ali Dorgan's pre-consultation questionnaire for personalised nutritional therapy in Cobh, Cork." />

      {/* Restore prompt */}
      <AnimatePresence>
        {showRestore && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-white shadow-medium rounded-2xl p-6 max-w-md w-[90%]" data-testid="restore-prompt">
            <p className="text-charcoal font-medium mb-2">Saved application found</p>
            <p className="text-charcoal/60 text-sm mb-5">We found a saved application on this device. Would you like to continue where you left off?</p>
            <div className="flex gap-3">
              <Button onClick={restoreDraft} className="flex-1 bg-charcoal text-white rounded-xl" data-testid="restore-continue">Continue</Button>
              <Button onClick={startFresh} variant="outline" className="flex-1 rounded-xl" data-testid="restore-start-again">Start Again</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="py-16 md:py-20 bg-cream-100">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-4 block">Nutritional Therapy</span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-charcoal leading-tight mb-6">
              Pre-Consultation Questionnaire
            </h1>
            <p className="text-charcoal/70 text-base md:text-lg leading-relaxed">
              This questionnaire helps Ali understand your health background before your consultation. 
              Please complete each section as fully as you can. Ali will review your responses and follow up directly.
            </p>
          </div>
        </div>
      </section>

      {/* Progress */}
      <div className="sticky top-20 z-30 bg-white/90 backdrop-blur-md border-b border-border/30 py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-charcoal">Step {step} of {TOTAL_STEPS}</span>
            <span className="text-xs text-charcoal/50">{stepLabels[step - 1]}</span>
          </div>
          <div className="w-full bg-cream-100 rounded-full h-2">
            <div className="bg-sage-500 h-2 rounded-full transition-all duration-500" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} data-testid="progress-bar" />
          </div>
        </div>
      </div>

      {/* Form */}
      <section className="py-12 md:py-16 bg-cream-50 min-h-[60vh]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <Card className="bg-white border-0 shadow-soft rounded-2xl">
                  <CardContent className="p-6 md:p-10">
                    {step === 1 && <Step1 form={form} update={update} />}
                    {step === 2 && <Step2 form={form} update={update} />}
                    {step === 3 && <Step3 form={form} update={update} />}
                    {step === 4 && <Step4 form={form} update={update} />}
                    {step === 5 && <Step5 form={form} update={update} updateNested={updateNested} />}
                    {step === 6 && <Step6 form={form} update={update} />}
                    {step === 7 && <Step7 form={form} updateNested={updateNested} />}
                    {step === 8 && <Step8 form={form} updateNested={updateNested} />}
                    {step === 9 && <Step9 form={form} update={update} updateNested={updateNested} />}
                    {step === 10 && <Step10 form={form} update={update} />}
                    {step === 11 && <Step11 form={form} />}
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <Button variant="outline" onClick={prev} disabled={step === 1} className="rounded-xl gap-2" data-testid="form-prev">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              {step < TOTAL_STEPS ? (
                <Button onClick={next} className="rounded-xl gap-2 bg-charcoal hover:bg-charcoal/90 text-white" data-testid="form-next">
                  Next <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="rounded-xl gap-2 bg-sage-600 hover:bg-sage-700 text-white px-8" data-testid="form-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Check className="w-4 h-4" /> Submit Application</>}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ======================= FORM STEPS =======================

const Label = ({ children, required }) => (
  <label className="block text-sm font-medium text-charcoal mb-2">
    {children} {required && <span className="text-red-400">*</span>}
  </label>
);

const StepTitle = ({ children }) => (
  <h2 className="font-serif text-2xl font-light text-charcoal mb-6">{children}</h2>
);

const inputClass = "h-11 bg-cream-50 border-border/50 rounded-xl";

const Step1 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-1">
    <StepTitle>General Information</StepTitle>
    <div className="grid sm:grid-cols-2 gap-5">
      <div><Label required>Name</Label><Input value={form.name} onChange={e => update('name', e.target.value)} className={inputClass} data-testid="input-name" /></div>
      <div><Label required>Email</Label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} className={inputClass} data-testid="input-email" /></div>
      <div><Label>Mobile</Label><Input type="tel" value={form.mobile} onChange={e => update('mobile', e.target.value)} className={inputClass} data-testid="input-mobile" /></div>
      <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => update('date_of_birth', e.target.value)} className={inputClass} data-testid="input-dob" /></div>
    </div>
    <div><Label>Address</Label><Input value={form.address} onChange={e => update('address', e.target.value)} className={inputClass} /></div>
    <div className="grid sm:grid-cols-2 gap-5">
      <div><Label>Occupation</Label><Input value={form.occupation} onChange={e => update('occupation', e.target.value)} className={inputClass} /></div>
      <div><Label>Date of last blood test</Label><Input value={form.last_blood_test} onChange={e => update('last_blood_test', e.target.value)} placeholder="e.g. January 2025" className={inputClass} /></div>
    </div>
    <div className="grid sm:grid-cols-3 gap-5">
      <div><Label>Height</Label><Input value={form.height} onChange={e => update('height', e.target.value)} placeholder="e.g. 5'8&quot; or 173cm" className={inputClass} /></div>
      <div><Label>Weight</Label><Input value={form.weight} onChange={e => update('weight', e.target.value)} placeholder="e.g. 75kg" className={inputClass} /></div>
      <div><Label>Do you smoke?</Label><Input value={form.smoker} onChange={e => update('smoker', e.target.value)} placeholder="Yes / No" className={inputClass} /></div>
    </div>
  </div>
);

const Step2 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-2">
    <StepTitle>Health Goals & Background</StepTitle>
    <div>
      <Label required>Reason for consultation / Your main health concerns</Label>
      <Textarea value={form.health_concerns} onChange={e => update('health_concerns', e.target.value)} rows={5} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="Please describe why you are seeking nutritional therapy..." data-testid="input-health-concerns" />
    </div>
    <div>
      <Label>Any recent stressful events (in the last 10 years)?</Label>
      <Textarea value={form.stressful_events} onChange={e => update('stressful_events', e.target.value)} rows={3} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="e.g. bereavement, job change, illness..." />
    </div>
    <div>
      <Label>Have you had any colonoscopy or scope tests done recently?</Label>
      <Textarea value={form.colonoscopy_results} onChange={e => update('colonoscopy_results', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="If yes, please include results..." />
    </div>
    <div>
      <Label>Have you seen a Nutritionist before?</Label>
      <Textarea value={form.seen_nutritionist} onChange={e => update('seen_nutritionist', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="If yes, please give details..." />
    </div>
  </div>
);

const Step3 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-3">
    <StepTitle>Medication & Supplements</StepTitle>
    <p className="text-charcoal/60 text-sm -mt-3 mb-4">Please list any current or past medication and any supplements you are currently taking.</p>
    <div>
      <Label>Current Medication</Label>
      <p className="text-charcoal/50 text-xs mb-2">Include: medication name, who prescribed it, how long you have been taking it, and dosage</p>
      <Textarea value={form.current_medications} onChange={e => update('current_medications', e.target.value)} rows={4} className="bg-cream-50 border-border/50 rounded-xl resize-none" />
    </div>
    <div>
      <Label>Past Medication</Label>
      <Textarea value={form.past_medications} onChange={e => update('past_medications', e.target.value)} rows={3} className="bg-cream-50 border-border/50 rounded-xl resize-none" />
    </div>
    <div>
      <Label>Health Supplements</Label>
      <p className="text-charcoal/50 text-xs mb-2">Vitamins, minerals, probiotics, herbal remedies, etc.</p>
      <Textarea value={form.supplements} onChange={e => update('supplements', e.target.value)} rows={3} className="bg-cream-50 border-border/50 rounded-xl resize-none" />
    </div>
  </div>
);

const Step4 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-4">
    <StepTitle>Medical History</StepTitle>
    <p className="text-charcoal/60 text-sm -mt-3 mb-4">Please list your illnesses, operations, and related medication/treatment from childhood to the present day.</p>
    <div>
      <Label>Illnesses, operations, and related medication/treatment</Label>
      <Textarea value={form.medical_history} onChange={e => update('medical_history', e.target.value)} rows={8} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="e.g.&#10;1995 - Tonsils removed&#10;2010 - Knee surgery, prescribed anti-inflammatories&#10;2020 - Diagnosed with IBS" />
    </div>
  </div>
);

const familyConditions = [
  'Heart Disease', 'High Blood Pressure', 'High Cholesterol', 'Osteoarthritis',
  'Rheumatoid Arthritis', 'Thyroid Problems', 'Osteoporosis', 'Allergies',
  'Obesity', 'Asthma', 'Depression', 'Alcoholism', 'Cancer',
  'Type 1 Diabetes', 'Type 2 Diabetes (late onset)'
];
const familyMembers = ['grandparents', 'parents', 'siblings', 'cousins', 'offspring'];
const familyMemberLabels = { grandparents: 'Grand-parents', parents: 'Parents', siblings: 'Siblings', cousins: 'Cousins', offspring: 'Offspring' };

const Step5 = ({ form, updateNested }) => {
  const fh = form.family_history || {};
  const toggleCell = (condKey, member) => {
    const current = fh[condKey]?.[member] || '';
    const next = current ? '' : 'Y';
    const row = { ...(fh[condKey] || {}), [member]: next };
    updateNested('family_history', condKey, row);
  };

  return (
    <div className="space-y-5" data-testid="step-5">
      <StepTitle>Family History</StepTitle>
      <p className="text-charcoal/60 text-sm -mt-3 mb-4">Please indicate if any of the following conditions exist in your family. Mark the relevant family member(s) for each condition.</p>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-sage-200">
              <th className="text-left py-3 pr-4 font-medium text-charcoal">Condition</th>
              {familyMembers.map(m => (
                <th key={m} className="py-3 px-3 font-medium text-charcoal text-center text-xs">{familyMemberLabels[m]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {familyConditions.map(cond => {
              const key = cond.toLowerCase().replace(/[^a-z0-9]/g, '_');
              return (
                <tr key={key} className="border-b border-border/20 hover:bg-cream-50 transition-colors">
                  <td className="py-2.5 pr-4 text-charcoal text-sm">{cond}</td>
                  {familyMembers.map(member => (
                    <td key={member} className="py-2.5 px-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggleCell(key, member)}
                        className={`w-8 h-8 rounded-lg border-2 transition-all text-xs font-bold ${
                          fh[key]?.[member] ? 'bg-sage-500 border-sage-500 text-white' : 'border-border/40 text-transparent hover:border-sage-300'
                        }`}
                      >
                        {fh[key]?.[member] ? 'Y' : ''}
                      </button>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked */}
      <div className="md:hidden space-y-4">
        {familyConditions.map(cond => {
          const key = cond.toLowerCase().replace(/[^a-z0-9]/g, '_');
          return (
            <div key={key} className="bg-cream-50 rounded-xl p-4">
              <p className="text-charcoal font-medium text-sm mb-3">{cond}</p>
              <div className="flex flex-wrap gap-2">
                {familyMembers.map(member => (
                  <button
                    key={member}
                    type="button"
                    onClick={() => toggleCell(key, member)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      fh[key]?.[member] ? 'bg-sage-500 text-white' : 'bg-white border border-border/40 text-charcoal/60 hover:border-sage-300'
                    }`}
                  >
                    {familyMemberLabels[member]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Step6 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-6">
    <StepTitle>Past Lifestyle</StepTitle>
    <div><Label>Were you breastfed?</Label><Input value={form.breastfed} onChange={e => update('breastfed', e.target.value)} placeholder="Yes / No / Unsure" className={inputClass} /></div>
    <div><Label>Did you receive all childhood vaccinations?</Label><Input value={form.childhood_vaccinations} onChange={e => update('childhood_vaccinations', e.target.value)} placeholder="Yes / No / Unsure" className={inputClass} /></div>
    <div><Label>Were you generally a happy, active child?</Label><Input value={form.happy_active_child} onChange={e => update('happy_active_child', e.target.value)} placeholder="Yes / No" className={inputClass} /></div>
    <div><Label>Any childhood trauma or adverse events?</Label><Textarea value={form.childhood_trauma} onChange={e => update('childhood_trauma', e.target.value)} rows={3} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="If yes, please describe briefly..." /></div>
  </div>
);

// Yes / No / Occasionally picker
const YNOPicker = ({ value, onChange }) => (
  <div className="flex gap-2">
    {['Yes', 'No', 'Occasionally'].map(opt => (
      <button key={opt} type="button" onClick={() => onChange(value === opt ? '' : opt)}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          value === opt ? 'bg-sage-500 text-white' : 'bg-cream-50 border border-border/40 text-charcoal/60 hover:border-sage-300'
        }`}>
        {opt === 'Occasionally' ? 'Occ.' : opt}
      </button>
    ))}
  </div>
);

const ProfileQuestion = ({ label, section, qKey, form, updateNested, hasDetail, detailPlaceholder }) => {
  const profile = form[section] || {};
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-2 border-b border-border/20">
      <span className="text-charcoal text-sm flex-1 min-w-0">{label}</span>
      <div className="flex items-center gap-2 shrink-0">
        <YNOPicker value={profile[qKey] || ''} onChange={v => updateNested(section, qKey, v)} />
      </div>
      {hasDetail && profile[qKey] && profile[qKey] !== 'No' && (
        <Input
          value={profile[`${qKey}_detail`] || ''}
          onChange={e => updateNested(section, `${qKey}_detail`, e.target.value)}
          placeholder={detailPlaceholder || 'Please specify...'}
          className="h-9 bg-cream-50 border-border/50 rounded-lg text-sm mt-1 sm:mt-0 sm:w-40"
        />
      )}
    </div>
  );
};

const digestiveQuestions = [
  { key: 'belching', label: 'Belching', hasDetail: true },
  { key: 'heartburn', label: 'Heartburn', hasDetail: true },
  { key: 'acid_reflux', label: 'Acid reflux or night time reflux', hasDetail: true },
  { key: 'antacids', label: 'Use of antacids', hasDetail: true },
  { key: 'coated_tongue', label: 'Coated tongue' },
  { key: 'stomach_upsets', label: 'Frequent stomach upsets', hasDetail: true, detailPlaceholder: 'How often?' },
  { key: 'greasy_foods', label: 'Stomach upset by greasy foods' },
  { key: 'skip_breakfast', label: 'Feel like skipping breakfast' },
  { key: 'bloating', label: 'Bloating', hasDetail: true, detailPlaceholder: 'When does it happen?' },
  { key: 'abdominal_pains', label: 'Abdominal pains or cramps', hasDetail: true },
  { key: 'diarrhoea', label: 'Diarrhoea' },
  { key: 'alternating_constipation', label: 'Alternating constipation and diarrhoea' },
  { key: 'bowel_frequency', label: 'How often do you have a bowel movement?', hasDetail: true, detailPlaceholder: 'e.g. once daily' },
  { key: 'black_stools', label: 'Black or tarry stools' },
  { key: 'undigested_food', label: 'Undigested food in stools' },
  { key: 'light_stools', label: 'Light clay coloured stools' },
  { key: 'greasy_stools', label: 'Greasy or shiny stools' },
  { key: 'anus_itching', label: 'Anus itching' },
  { key: 'parasites', label: 'Known history of parasites, worms or bacterial infestations' },
  { key: 'blood_in_stool', label: 'Blood in stool', hasDetail: true },
  { key: 'mucous_in_stool', label: 'Mucous in stool', hasDetail: true },
];

const Step7 = ({ form, updateNested }) => (
  <div className="space-y-3" data-testid="step-7">
    <StepTitle>Digestive Tract Profile</StepTitle>
    <p className="text-charcoal/60 text-sm -mt-3 mb-4">For each question, select Yes, No, or Occasionally. Add detail where prompted.</p>
    {digestiveQuestions.map(q => (
      <ProfileQuestion key={q.key} label={q.label} section="digestive_profile" qKey={q.key}
        form={form} updateNested={updateNested} hasDetail={q.hasDetail} detailPlaceholder={q.detailPlaceholder} />
    ))}
  </div>
);

const liverQuestions = [
  { key: 'nausea', label: 'Frequent nausea' },
  { key: 'motion_sickness', label: 'Tendency to motion sickness' },
  { key: 'bitter_taste', label: 'Bitter taste in mouth especially after meals' },
  { key: 'caffeine_reaction', label: 'Strong reaction to caffeine', hasDetail: true, detailPlaceholder: 'What reaction?' },
  { key: 'haemorrhoids', label: 'Haemorrhoids' },
];

const endocrineQuestions = [
  { key: 'difficulty_sleeping', label: 'Difficulty sleeping' },
  { key: 'hard_mornings', label: 'Hard to get going in the morning' },
  { key: 'frequent_fatigue', label: 'Frequent fatigue' },
  { key: 'drowsiness', label: 'Frequent drowsiness during the day', hasDetail: true, detailPlaceholder: 'When?' },
  { key: 'dizziness_hungry', label: 'Dizziness or irritability if you don\'t eat often' },
  { key: 'sweating', label: 'Frequent sweating' },
  { key: 'excessive_thirst', label: 'Excessive thirst' },
  { key: 'reduced_energy', label: 'Reduced energy' },
  { key: 'depression', label: 'Tendency to depression or social isolation' },
  { key: 'temp_intolerance', label: 'Intolerance to cold or heat' },
  { key: 'cold_extremities', label: 'Cold hands and feet' },
  { key: 'headaches', label: 'Frequent headaches or migraines' },
  { key: 'anxiety', label: 'Nervousness or anxiety' },
  { key: 'teeth_grinding', label: 'Teeth-grinding' },
];

const Step8 = ({ form, updateNested }) => (
  <div className="space-y-6" data-testid="step-8">
    <div>
      <StepTitle>Liver Profile</StepTitle>
      <div className="space-y-3">
        {liverQuestions.map(q => (
          <ProfileQuestion key={q.key} label={q.label} section="liver_profile" qKey={q.key}
            form={form} updateNested={updateNested} hasDetail={q.hasDetail} detailPlaceholder={q.detailPlaceholder} />
        ))}
      </div>
    </div>
    <div className="pt-4">
      <StepTitle>Endocrine (Hormonal) System</StepTitle>
      <div className="space-y-3">
        {endocrineQuestions.map(q => (
          <ProfileQuestion key={q.key} label={q.label} section="endocrine_profile" qKey={q.key}
            form={form} updateNested={updateNested} hasDetail={q.hasDetail} detailPlaceholder={q.detailPlaceholder} />
        ))}
      </div>
    </div>
  </div>
);

const immuneQuestions = [
  { key: 'runny_nose', label: 'Runny or drippy nose', hasDetail: true, detailPlaceholder: 'How often?' },
  { key: 'frequent_infections', label: 'Frequent infections/colds and flu' },
  { key: 'antibiotic_use', label: 'Frequent antibiotic use', hasDetail: true, detailPlaceholder: 'How often?' },
  { key: 'cystitis', label: 'Frequent cystitis', hasDetail: true },
  { key: 'thrush', label: 'Frequent thrush', hasDetail: true },
  { key: 'chronic_viral', label: 'History of chronic viral conditions', hasDetail: true, detailPlaceholder: 'Which conditions?' },
  { key: 'inflammatory', label: 'Inflammatory conditions', hasDetail: true, detailPlaceholder: 'Which conditions?' },
];

const Step9 = ({ form, update, updateNested }) => (
  <div className="space-y-6" data-testid="step-9">
    <div>
      <StepTitle>Immune Profile</StepTitle>
      <div className="space-y-3">
        {immuneQuestions.map(q => (
          <ProfileQuestion key={q.key} label={q.label} section="immune_profile" qKey={q.key}
            form={form} updateNested={updateNested} hasDetail={q.hasDetail} detailPlaceholder={q.detailPlaceholder} />
        ))}
      </div>
    </div>
    <div className="pt-4">
      <StepTitle>Exercise</StepTitle>
      <div className="space-y-5">
        <div><Label>How many times a week do you exercise?</Label><Input value={form.exercise_frequency} onChange={e => update('exercise_frequency', e.target.value)} className={inputClass} placeholder="e.g. 3 times" /></div>
        <div><Label>What type of exercise?</Label><Input value={form.exercise_type} onChange={e => update('exercise_type', e.target.value)} className={inputClass} placeholder="e.g. running, gym, walking..." /></div>
        <div><Label>Why do you exercise? (Or if you don't, why not?)</Label><Textarea value={form.exercise_reason} onChange={e => update('exercise_reason', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" /></div>
      </div>
    </div>
  </div>
);

const Step10 = ({ form, update }) => (
  <div className="space-y-5" data-testid="step-10">
    <StepTitle>Food Diary</StepTitle>
    <p className="text-charcoal/60 text-sm -mt-3 mb-4">Please describe what you typically eat and drink on a normal day.</p>
    <div className="grid sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2"><Label>Breakfast</Label><Textarea value={form.breakfast} onChange={e => update('breakfast', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" /></div>
      <div><Label>Typical breakfast time</Label><Input value={form.breakfast_time} onChange={e => update('breakfast_time', e.target.value)} placeholder="e.g. 8:00am" className={inputClass} /></div>
    </div>
    <div><Label>Lunch</Label><Textarea value={form.lunch} onChange={e => update('lunch', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" /></div>
    <div className="grid sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2"><Label>Dinner</Label><Textarea value={form.dinner} onChange={e => update('dinner', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" /></div>
      <div><Label>Typical dinner time</Label><Input value={form.dinner_time} onChange={e => update('dinner_time', e.target.value)} placeholder="e.g. 7:00pm" className={inputClass} /></div>
    </div>
    <div className="grid sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2"><Label>Snacks</Label><Textarea value={form.snacks} onChange={e => update('snacks', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" /></div>
      <div><Label>Snack times</Label><Input value={form.snack_times} onChange={e => update('snack_times', e.target.value)} placeholder="e.g. 11am, 3pm" className={inputClass} /></div>
    </div>
    <div><Label>Any other regular foods or drinks?</Label><Textarea value={form.other_foods} onChange={e => update('other_foods', e.target.value)} rows={2} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="e.g. coffee, alcohol, fizzy drinks..." /></div>
    <div><Label>Additional information</Label><Textarea value={form.additional_info} onChange={e => update('additional_info', e.target.value)} rows={3} className="bg-cream-50 border-border/50 rounded-xl resize-none" placeholder="Anything else you would like Ali to know..." /></div>
  </div>
);

const Step11 = ({ form }) => (
  <div className="space-y-5" data-testid="step-11">
    <StepTitle>Review Your Application</StepTitle>
    <p className="text-charcoal/60 text-sm -mt-3 mb-6">Please review your information before submitting. You can go back to any section to make changes.</p>
    <div className="space-y-4">
      {[
        ['General Information', [['Name', form.name], ['Email', form.email], ['Mobile', form.mobile], ['DOB', form.date_of_birth]]],
        ['Health Goals', [['Main concerns', form.health_concerns]]],
        ['Medication', [['Current', form.current_medications], ['Supplements', form.supplements]]],
        ['Medical History', [['History', form.medical_history]]],
        ['Exercise', [['Frequency', form.exercise_frequency], ['Type', form.exercise_type]]],
        ['Food Diary', [['Breakfast', form.breakfast], ['Lunch', form.lunch], ['Dinner', form.dinner]]],
      ].map(([title, fields]) => {
        const filled = fields.filter(([, v]) => v);
        if (filled.length === 0) return null;
        return (
          <div key={title} className="bg-cream-50 rounded-xl p-4">
            <h4 className="font-medium text-charcoal text-sm mb-2">{title}</h4>
            {filled.map(([label, value]) => (
              <p key={label} className="text-charcoal/60 text-sm"><span className="text-charcoal/80 font-medium">{label}:</span> {typeof value === 'string' ? (value.length > 80 ? value.substring(0, 80) + '...' : value) : 'Provided'}</p>
            ))}
          </div>
        );
      })}
    </div>
    <div className="bg-sage-50 rounded-xl p-4 mt-6">
      <p className="text-charcoal/70 text-sm">
        By submitting, you confirm that the information provided is accurate to the best of your knowledge. 
        Ali will review your application and follow up with you directly.
      </p>
    </div>
  </div>
);

export default NutritionApplication;
